import { useEffect, useState } from "react";

/**
 * Default breakpoint for "mobile viewport": anything narrower than
 * Tailwind's `md` breakpoint (768px). Keep this in sync with
 * `tailwind.config.js` so callers can reason about the same mobile boundary
 * the rest of the app uses.
 */
const DEFAULT_MAX_WIDTH_PX = 767;

interface UseMobileViewportOptions {
  /** Maximum viewport width (inclusive, in CSS pixels) that is considered mobile. */
  maxWidthPx?: number;
}

/**
 * Reactive flag indicating whether the current viewport is narrower than the
 * mobile breakpoint.
 *
 * When to reach for this hook vs. `userIsMobile` from `UserContext`:
 * - Use `useMobileViewport` for **responsive layout** decisions that should
 *   follow the viewport (e.g. stack graph controls vertically on narrow
 *   screens, swap column count on resize, pick between inline and collapsed
 *   layouts in dev-tools mobile emulation).
 * - Use `userIsMobile` for **device-affordance** decisions that should follow
 *   the actual device (e.g. render a bottom Drawer instead of a centered
 *   Modal, tweak tap-target sizing for touch).
 *
 * Key differences:
 * - This hook derives from `window.matchMedia` and reacts to viewport changes
 *   (resize, orientation, window snapping). `userIsMobile` is derived from
 *   `react-device-detect` (user-agent sniff) and is set once in an effect, so
 *   a desktop window resized narrow does not flip it, and the first paint can
 *   briefly disagree with the final value.
 * - On the first render this hook returns the correct value synchronously via
 *   a lazy initialiser, which avoids the layout-flash `userIsMobile` can
 *   cause on mobile devices.
 *
 * Safe to call during SSR: the hook assumes "not mobile" when `window` is
 * unavailable and updates on mount once a matchMedia listener can be
 * attached.
 */
export function useMobileViewport(
  { maxWidthPx = DEFAULT_MAX_WIDTH_PX }: UseMobileViewportOptions = {}
): boolean {
  const getInitialIsMobile = (): boolean => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia(`(max-width: ${maxWidthPx}px)`).matches;
  };

  const [isMobile, setIsMobile] = useState<boolean>(getInitialIsMobile);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mediaQuery = window.matchMedia(`(max-width: ${maxWidthPx}px)`);
    const handleChange = (event: MediaQueryListEvent): void => {
      setIsMobile(event.matches);
    };

    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, [maxWidthPx]);

  return isMobile;
}

export default useMobileViewport;
